import i18n from "i18next";
import { initReactI18next } from "react-i18next";

const en = {
  translation: {
    // The brand *name* is per-instance config, not a translation -- it comes
    // from GET /api/branding via <Wordmark />. `tagline` stays here so the
    // shipped product has one in the viewer's own language; a configured
    // BRAND_TAGLINE overrides it in every language (see Layout.jsx).
    app: { tagline: "Client Relationship Manager" },
    nav: {
      dashboard: "Dashboard",
      contacts: "Contacts",
      companies: "Companies",
      deals: "Deals",
      projects: "Projects",
      activities: "Activities",
      meetings: "Meetings",
      calendar: "Calendar",
      utilization: "Utilization",
      dealFlow: "Deal flow",
      users: "Users",
      settings: "Settings",
      logout: "Log out",
      more: "More",
    },
    onboarding: {
      replayTour: "Replay tour",
      back: "Back",
      close: "Close",
      finish: "Finish",
      next: "Next",
      skip: "Skip",
      welcomeTitle: "Welcome to {{brand}}!",
      welcomeBody: "Let's walk through the whole app together — a few minutes, and you'll know where everything lives. You can skip out at any point.",
      metricsTitle: "Your dashboard",
      metricsBody: "Contacts, open deals, pipeline value, cash flow, open tasks — all at a glance.",
      aiTitle: "AI command bar",
      aiBody: "Type a plain-English command here, like \"create a deal for Acme worth 5000 {{currency}}\", and the AI will act on it.",
      pipelineTitle: "Pipeline chart",
      pipelineBody: "See your deals by stage, in {{currencies}}.",
      tasksTitle: "Upcoming tasks",
      tasksBody: "Your open tasks and follow-ups, sorted by due date.",
      navTitle: "Your navigation",
      navBody: "Every part of the CRM lives here. Let's visit them one by one.",
      navMobileTitle: "Your navigation",
      navMobileBody: "The main sections live in this bar. Let's visit them one by one.",
      moreTitle: "The “More” tab",
      moreBody: "Companies, Activities — and, if you're an admin, Users and Settings — live behind More: the bar has no room for them.",
      contactsAddTitle: "Adding a contact",
      contactsAddBody: "“New contact” creates a person: name, email, phone, the company they work at, and a status from lead through to customer.",
      contactsFindTitle: "Finding people",
      contactsFindBody: "Search by name or email, filter by status, and group the list by status or company. The card/list toggle is up top.",
      companiesAddTitle: "Adding a company",
      companiesAddBody: "“New company” records the organization behind your contacts. Give it a website and the CRM fetches its logo for you.",
      companiesViewsTitle: "Cards or list",
      companiesViewsBody: "Switch between card and list view, and filter by industry, size, or whether you have a website on file.",
      dealsAddTitle: "Adding a deal",
      dealsAddBody: "“New deal” is where a lead becomes a number: title, company, contact, value and currency, stage, and where it came from. Leave it unassigned and it lands in the shared inbox for anyone to claim.",
      dealsBoardTitle: "Your pipeline",
      dealsBoardBody: "Board or list view. On the board, drag a card between columns to move a deal along — win one and its project is created for you automatically.",
      dealsInboxTitle: "Who owes whom",
      dealsInboxBody: "Filter for unclaimed leads, deals where the ball is in our court, or ones that have gone stale. Open a deal to claim it, hand it over, or read its full timeline.",
      projectsTitle: "Projects",
      projectsBody: "Won deals become projects. Track the budget, log hours against it, and bill by milestone — from a deposit-and-final split to a full milestone plan.",
      activitiesTitle: "Activities",
      activitiesBody: "Calls, emails and tasks, logged against any contact, deal or project. Whether one is inbound or outbound is what moves a deal's ball-in-court.",
      calendarTitle: "Calendar",
      calendarBody: "Everything with a date, in one place — switch between month, week, 3-day and day views. Click an event for details, or drag it to reschedule.",
      reportsTitle: "Reports",
      reportsBody: "Team utilization by week or month, plus pipeline analytics under Deal flow: win/loss ratio and how long deals sit in each stage.",
      notifTitle: "Notifications",
      notifBody: "Get nudged about stale deals, unclaimed leads, and overdue tasks here.",
      themeTitle: "Language & theme",
      themeBody: "Switch between English and Hungarian, or light and dark, any time.",
      closingTitle: "That's the tour!",
      closingBody: "Replay it whenever you like from the ? button. Happy selling!",
    },
    auth: {
      welcome: "Welcome back",
      subtitle: "Sign in to your {{brand}} workspace",
      pitch: "The mobile-first CRM for small teams. Contacts, deals, projects and an AI command bar — in one place.",
      email: "Email",
      password: "Password",
      name: "Full name",
      signIn: "Sign in",
      signUp: "Create account",
      noAccount: "Don't have an account?",
      haveAccount: "Already have an account?",
      register: "Register",
      login: "Sign in",
    },
    common: {
      search: "Search…",
      add: "Add",
      new: "New",
      edit: "Edit",
      delete: "Delete",
      save: "Save",
      cancel: "Cancel",
      create: "Create",
      update: "Update",
      confirm: "Confirm",
      loading: "Loading…",
      noResults: "No results found",
      actions: "Actions",
      all: "All",
      close: "Close",
      confirmDelete: "Are you sure you want to delete this?",
      readOnly: "Read-only access",
    },
    dashboard: {
      title: "Dashboard",
      greeting: "Welcome back",
      contacts: "Contacts",
      companies: "Companies",
      openDeals: "Open deals",
      activeProjects: "Active projects",
      pipelineValue: "Pipeline value",
      wonValue: "Won this period",
      cashFlow: "Invoiced, awaiting payment",
      openTasks: "Open tasks",
      pipelineByStage: "Pipeline by stage",
      contactsByStatus: "Contacts by status",
      projectsByStatus: "Projects by status",
      upcomingTasks: "Upcoming tasks",
    },
    contact: {
      title: "Contacts",
      first_name: "First name",
      last_name: "Last name",
      email: "Email",
      phone: "Phone",
      jobtitle: "Job title",
      status: "Status",
      company: "Company",
      notes: "Notes",
      tags: "Tags",
      newContact: "New contact",
      editContact: "Edit contact",
      regularReferrer: "Regular referrer",
      referrals: "Referrals",
      referralsCount: "Deals referred",
      referralsWon: "Won",
      referrersOnly: "Referrers only",
      linkedinUrl: "LinkedIn",
      grid: "Grid",
      list: "List",
      sortBy: "Sort by",
      groupBy: "Group by",
      groupNone: "No grouping",
      groupByStatus: "By status",
      groupByCompany: "By company",
      sort_newest: "Newest first",
      sort_oldest: "Oldest first",
      sort_name_asc: "Name (A–Z)",
      sort_name_desc: "Name (Z–A)",
      sort_company_asc: "Company (A–Z)",
      sort_status_pipeline: "Status (pipeline order)",
      allCompanies: "All companies",
      uncategorized: "No company",
    },
    company: {
      title: "Companies",
      name: "Company name",
      industry: "Industry",
      website: "Website",
      phone: "Phone",
      email: "Email",
      address: "Address",
      size: "Company size",
      notes: "Notes",
      newCompany: "New company",
      editCompany: "Edit company",
      refreshLogo: "Refresh logo",
      grid: "Grid",
      list: "List",
      sortBy: "Sort by",
      groupBy: "Group by",
      groupNone: "No grouping",
      groupByIndustry: "By industry",
      groupBySize: "By size",
      sort_newest: "Newest first",
      sort_oldest: "Oldest first",
      sort_name_asc: "Name (A–Z)",
      sort_name_desc: "Name (Z–A)",
      sort_industry_asc: "Industry (A–Z)",
      sort_size_asc: "Size (A–Z)",
      hasWebsite: "Has website",
      allIndustries: "All industries",
      allSizes: "All sizes",
      uncategorized: "Uncategorized",
    },
    deal: {
      title: "Deals",
      name: "Deal title",
      value: "Value",
      currency: "Currency",
      stage: "Stage",
      probability: "Probability",
      expectedClose: "Expected close",
      company: "Company",
      contact: "Contact",
      notes: "Notes",
      newDeal: "New deal",
      editDeal: "Edit deal",
      board: "Board",
      list: "List",
      total: "Total",
      owner: "Owner",
      unassigned: "Unassigned",
      claim: "Claim",
      source: "Source",
      source_inbound: "Inbound",
      source_outreach: "Outreach",
      source_referral: "Referral",
      source_other: "Other",
      ballInCourt: "Ball in court",
      ballInCourtFilter: "Our turn",
      ballInCourt_us: "Us",
      ballInCourt_them: "Them",
      ballInCourt_none: "—",
      leadType: "Lead type",
      leadType_single: "Single-sided",
      leadType_double: "Double-sided",
      leadTypeHint: "Double-sided: the paying/contracting party is a different company or contact than who you'll be working with day-to-day.",
      contractCompany: "Contracting company",
      contractContact: "Contracting contact",
      referredBy: "Referred by",
      stale: "Stale",
    },
    project: {
      title: "Projects",
      name: "Project name",
      description: "Description",
      status: "Status",
      priority: "Priority",
      budget: "Budget",
      startDate: "Start date",
      endDate: "End date",
      company: "Company",
      newProject: "New project",
      editProject: "Edit project",
      followUpDays: "Follow-up delay (days)",
      closedAt: "Closed",
    },
    followup: {
      title: "Follow-up check-in",
      hint: "Record how the client felt about this project, and log a referral if they mentioned one.",
      satisfaction: "Satisfaction (1-5)",
      hasReferral: "They mentioned a referral",
      referredContact: "Referred contact",
      complete: "Complete follow-up",
      readOnlyHint: "Waiting for someone with write access to complete this.",
    },
    milestone: {
      title: "Milestones",
      add: "Add milestone",
      edit: "Edit milestone",
      name: "Milestone name",
      dueDate: "Due date",
      byAmount: "Fixed amount",
      byPercentage: "Percentage",
      amount: "Amount",
      percentage: "Percentage",
      valueRequired: "Enter an amount or percentage.",
      none: "No milestones yet",
      budgetMismatch: "Milestone total doesn't match the project budget -- double-check the amounts.",
      totalVsBudget: "{{total}} of {{budget}} budget",
      template: "Milestone template",
      template_single_final: "Single payment on delivery",
      template_deposit_final: "Deposit + final payment",
      template_milestones: "Custom (start empty)",
      workStatus_in_progress: "In progress",
      workStatus_client_review: "Client review",
      workStatus_accepted: "Accepted",
      paymentStatus_not_due: "Not due",
      paymentStatus_invoiceable: "Invoiceable",
      paymentStatus_invoiced: "Invoiced",
      paymentStatus_paid: "Paid",
    },
    activity: {
      title: "Activities",
      type: "Type",
      directionLabel: "Direction",
      direction: { inbound: "Inbound", outbound: "Outbound", internal: "Internal" },
      subject: "Subject",
      description: "Description",
      dueDate: "Due date",
      completed: "Completed",
      newActivity: "New activity",
      editActivity: "Edit activity",
      logActivity: "Log activity",
      noDirection: "—",
    },
    users: {
      title: "Users & Roles",
      name: "Name",
      email: "Email",
      role: "Role",
      status: "Status",
      active: "Active",
      disabled: "Disabled",
      newUser: "New user",
      editUser: "Edit user",
      password: "Password",
    },
    branding: {
      identityGroup: "Identity",
      metadataGroup: "Browser & AI metadata",
      metadataHint: "The browser tab title, the description search engines show, and the one-line description of your business that the AI features are told about.",
      coloursGroup: "Colours",
      coloursHint: "Applied instantly across the app. Check both light and dark in the preview — a colour that looks sharp on white can vanish on the dark background.",
      domainsGroup: "Domains",
      domainsHint: "Meeting participants on these domains are treated as internal; everyone else is client-side.",
      moneyGroup: "Money",
      moneyHint: "The currencies this instance accepts, and which one new deals and projects default to. The default must be one of the listed codes.",
      title: "Branding",
      desc: "Name, wordmark, colours and currencies for this instance. Applies immediately — no rebuild.",
      orgName: "Organisation name",
      tagline: "Tagline",
      taglinePlaceholder: "Leave blank to keep the translated default",
      wordmarkText: "Wordmark",
      wordmarkAccent: "Wordmark accent",
      preview: "Preview",
      previewApp: "App",
      previewLogin: "Sign-in",
      previewMobile: "Mobile",
      previewLight: "Preview in light theme",
      previewDark: "Preview in dark theme",
      previewHint: "A mock-up of your unsaved settings. Switch surfaces and themes to check a colour works everywhere before you save.",
      discovery: {
        title: "Fill this in from a website",
        desc: "We read the site's public brand details — name, colours, logos — and suggest them. Nothing is changed until you pick what to keep.",
        websiteLabel: "Company website",
        fetch: "Read website",
        fetching: "Reading…",
        found: "{{count}} suggestion available",
        found_plural: "{{count}} suggestions available",
        selectAll: "Select all",
        selectNone: "Clear",
        apply: "Apply {{count}} selected",
        applying: "Applying…",
        discard: "Discard",
        applyHint: "Images are downloaded and stored here, so your sign-in page never depends on another site staying up.",
        nothingNew: "Nothing new to suggest — this site's details already match your settings.",
        partial: "This took too long and the result is incomplete. Try reading the site again.",
      },
      documentTitle: "Browser tab title",
      orgDescription: "What this organisation does",
      orgDescriptionPlaceholder: "e.g. an engineering consultancy — used in AI prompts",
      primary: "Primary colour",
      primaryHover: "Primary (hover)",
      glow: "Accent colour",
      logoIconUrl: "Logo icon URL",
      faviconUrl: "Favicon URL",
      loginImageUrl: "Login artwork URL",
      loginImagePlaceholder: "Blank uses a gradient from your colours",
      internalDomains: "Internal email domains",
      currencies: "Currencies",
      defaultCurrency: "Default currency",
      images: "Images",
      logoUrl: "Full logo",
      appleTouchIconUrl: "Apple touch icon",
      themeColor: "Mobile browser bar",
      metaDescription: "Meta description",
      googleHd: "Google account hint",
      upload: "Upload",
      removeUpload: "Remove the uploaded file",
      imagesHint: "PNG, JPEG, WebP, GIF or ICO — up to 512 KB (3 MB for the login image). SVG isn't accepted: it can contain scripts and these images are visible before sign-in. Removing an upload restores the built-in default.",
      hint: "Clearing a field restores the environment value or the built-in default.",
    },
    capabilities: {
      visibilityGroup: "New records",
      visibilityHint: "Whether a new deal or project starts visible to everyone, or only to its owner and the people added to it.",
      matrixGroup: "Capability matrix",
      matrixHint: "Admin and manager are fixed by design. Managing users and editing this matrix are always admin-only and never appear here.",
      title: "Access control",
      desc: "What user and guest roles can do. Admin and manager always have full access.",
      capability: "Capability",
      view_financials: "View financial values (deal value, budget, hourly rate)",
      manage_deals: "Create/edit/delete deals",
      manage_projects: "Create/edit/delete projects",
      invite_members: "Invite members to private deals/projects",
      set_visibility: "Change a deal/project's visibility",
      reassign_owner: "Reassign a deal/project's owner",
      reassignOwnerNote: "(not yet enforced anywhere -- toggling this has no effect yet)",
      view_all_reports: "View aggregated reports (not just their own)",
      defaultVisibility: "Default visibility for new deals/projects",
      public: "Public (visible to everyone)",
      private: "Private (invite-only)",
    },
    thresholds: {
      title: "Thresholds",
      desc: "How long something may sit before the CRM flags it.",
      businessDaysNote: "Counted in business days (Monday to Friday); public holidays are not taken into account.",
      unassigned: "Unclaimed lead",
      unassignedHint: "After this long with no owner, a lead is flagged to managers and admins.",
      awaitingResponse: "Awaiting our reply",
      awaitingResponseHint: "After this long with the ball in our court, the deal's owner is reminded.",
      stale: "Deal goes stale",
      staleHint: "After this long with no contact, the deal is marked stale in the pipeline.",
    },
    settings: {
      title: "Settings",
      nav: {
        branding: "Branding",
        ai: "AI",
        permissions: "Permissions",
        thresholds: "Thresholds",
        automation: "Automation",
        apiKeys: "API keys",
        integrations: "Integrations",
      },
      unsaved: "You have unsaved changes",
      discard: "Discard",
      saving: "Saving…",
      savedFlash: "Changes saved",
      aiCredentials: "Credentials",
      aiModel: "Model",
      aiKeyHint: "Stored encrypted in your own database and used only server-side. Get a free key at",
      housekeepingManual: "Run it now",
      housekeepingManualHint: "The job runs on its own every night at 02:00. Running it by hand is for checking what it does, or for catching up after downtime.",
      integrations: "Integrations",
      integrationsDesc: "Connect this CRM to the tools your team already uses.",
      integrationsRequires: "Both need Google Cloud OAuth credentials (a Client ID and Secret) before they can be switched on.",
      integrationGmail: "Gmail",
      integrationGmailDesc: "Log emails against contacts and deals automatically.",
      integrationCalendar: "Google Calendar",
      integrationCalendarDesc: "Keep meetings in step with your calendar in both directions.",
      integrationSignInGroup: "Sign in with Google",
      integrationSignInNote: "Google sign-in is configured on the server, not here — it needs GOOGLE_CLIENT_ID and GOOGLE_WORKSPACE_DOMAIN set for this deployment. The account-picker hint that goes with it is the “Google domain” field under Branding.",
      saLeastPrivilege: "Give each account only the capabilities its agent actually needs. Every write it makes is recorded in the audit trail against this account.",
      saEmptyHint: "Create one when you want an external AI agent or script to reach this CRM through the API.",
      ai: "AI Assistant (OpenRouter)",
      aiDesc: "Connect your OpenRouter API key to enable AI commands.",
      apiKey: "OpenRouter API key",
      model: "Model",
      configured: "Connected",
      notConfigured: "Not connected",
      google: "Google Workspace",
      googleDesc: "Connect Gmail & Calendar (requires Google Cloud credentials).",
      appearance: "Appearance",
      language: "Language",
      theme: "Theme",
      light: "Light",
      dark: "Dark",
      housekeeping: "Daily housekeeping job",
      housekeepingDesc: "Follow-up task creation, stale-deal flagging, and notification sync -- normally runs automatically once a day.",
      housekeepingRun: "Run now",
      housekeepingRunning: "Running…",
      housekeepingFollowUps: "Follow-up tasks created",
      housekeepingStale: "Stale flags changed",
      housekeepingNotifications: "Users synced",
      housekeepingSkipped: "Skipped -- already running.",
      serviceAccounts: "Service accounts",
      serviceAccountsDesc: "API-key principals for external tools and AI agents (e.g. the MCP server) -- same role/capability model as a human user.",
      newServiceAccount: "New service account",
      saName: "Name",
      saActive: "Active",
      saRevoked: "Revoked",
      saKeyWarning: "Copy this key now -- it won't be shown again.",
      saKeyCloseConfirm: "You haven't copied this key. It won't be shown again -- close anyway?",
      saCopy: "Copy",
      saCopied: "Copied",
      saRevokeConfirm: "Revoke this service account? Anything using its API key will stop working immediately.",
      noServiceAccounts: "No service accounts yet.",
    },
    ai: {
      placeholder: "Ask AI to create a project, contact, deal or summarize…",
      title: "AI Command",
      run: "Run",
      hint: "Try: “Create a project called RAG Pilot for Acme, high priority”",
      thinking: "Thinking…",
      created: "Created",
      notConfigured: "AI not configured. Add an OpenRouter key in Settings.",
    },
    time: {
      title: "Time & effort",
      internalHint: "Internal cost/utilization tracking only -- client billing runs off the milestones above, not the hourly rate.",
      logged: "Logged", estimated: "Estimated", remaining: "Remaining",
      addTime: "Log time", hours: "Hours", billable: "Billable (internal)",
      rate: "Hourly rate", amount: "Internal cost", noEntries: "No time logged yet",
      logHours: "Log hours",
    },
    health: {
      label: "Health", on_track: "On track", at_risk: "At risk",
      over_budget: "Over budget", completed: "Completed", cancelled: "Cancelled",
    },
    detail: {
      back: "Back", overview: "Overview", deals: "Deals", contacts: "Contacts",
      projects: "Projects", timeline: "Activity timeline", noActivities: "No activities yet",
      noItems: "Nothing here yet",
    },
    calendar: {
      title: "Calendar",
      today: "Today",
      previous: "Previous",
      next: "Next",
      noTasks: "No tasks this day",
      viewLabel: "Calendar view",
      view: { month: "Month", week: "Week", day3: "3 days", day: "Day" },
      // Keyboard shortcuts are physical keys, so they are not translated.
      viewKey: { month: "M", week: "W", day3: "X", day: "D" },
      allDay: "All day",
      allDayEvent: "All day — no specific time",
      startTime: "Start",
      endTime: "End",
      newEvent: "New event",
      editEvent: "Edit event",
      typeLocked: "This is a meeting — change its type on the meeting page.",
      subjectRequired: "Give it a subject so you'll recognise it later.",
      dateRequired: "Pick a valid date.",
      endBeforeStart: "The end has to come after the start.",
      hideCompleted: "Hide done",
      hidden: "{{count}} hidden by filters",
      more: "+{{count}} more",
      eventCount: "{{count}} entries",
      markDone: "Mark done",
      markOpen: "Reopen",
      openMeeting: "Open meeting",
      openDeal: "Open deal",
      openProject: "Open project",
      openContact: "Open contact",
      openCompany: "Open company",
      saved: "Saved",
      deleted: "Deleted",
      moved: "Rescheduled",
      emptyHint: "Nothing scheduled yet — click any day to add something.",
      emptyHintReadOnly: "Nothing scheduled in this range.",
      dragHint: "Drag an event to reschedule it.",
      undated: "{{count}} without a date, so not shown here:",
    },
    meet: {
      title: "Meetings",
      allStates: "All states",
      state: { draft: "Draft", processing: "Processing", ready: "Ready", failed: "Failed" },
      emptyTitle: "No meetings yet",
      emptyHint: "Drop a meeting transcript above — a .docx or .vtt exported from Teams, or plain text. We'll create the meeting around it and pull out a summary and action items.",
      openItems: "{{count}} open",
      dropHere: "Drop a transcript here, or click to choose a file",
      dropHint: "Teams export (.docx, .vtt) or plain text (.txt, .md)",
      uploading: "Uploading…",
      pasteInstead: "Paste text",
      pasteTitle: "Paste a transcript",
      pasteHint: "Paste the meeting transcript. Keep the speaker names — they're used to match participants to contacts.",
      pastePlaceholder: "Anna: Hi everyone, let's start…",
      pastedTitle: "Pasted transcript",
      startExtraction: "Start extraction",
      backToList: "All meetings",
      notFound: "This meeting doesn't exist, or you don't have access to it.",
      deal: "Deal",
      saved: "Saved",
      processingHint: "Extracting the summary and action items — this usually takes under a minute.",
      failedTitle: "Extraction failed",
      failedHint: "You can retry it, replace the transcript, or just write the notes by hand.",
      sensitiveHidden: "This meeting is marked sensitive — only its owner and admins can read the summary and transcript.",
      noTranscriptYet: "No transcript yet",
      summary: "Summary",
      humanEdited: "Edited by a human — the AI original is kept in the extraction history.",
      actionItems: "Action items",
      alreadyAccepted: "{{count}} already turned into tasks",
      itemState: { proposed: "Proposed", accepted: "Accepted", dismissed: "Dismissed" },
      accept: "Accept",
      viewTask: "View task",
      ownerDefault: "Meeting owner",
      createTask: "Create task",
      taskCreated: "Task created",
      decisions: "Decisions",
      risks: "Risks & open questions",
      transcript: "Transcript",
      chars: "characters",
      regenerate: "Regenerate",
      replaceTranscript: "Upload a better transcript",
      editTitle: "Edit meeting",
      subject: "Title",
      startsAt: "Starts",
      endsAt: "Ends",
      direction: "Who owes the next step?",
      dirInbound: "Us — we owe the follow-up",
      dirOutbound: "Them — we're waiting on the client",
      dirInternal: "Internal meeting",
      participants: "Participants",
      addTeammate: "Add a teammate…",
      sensitive: "Mark as sensitive",
      sensitiveHint: "Restricts the summary and transcript to the owner and admins.",
      confirmDelete: "Delete this meeting with its transcript and extractions? Tasks already created from it are kept.",
      dealCardTitle: "Meetings",
      seeAll: "See all",
      noneForDeal: "No meetings recorded for this deal yet.",
    },
    members: {
      title: "Access", public: "Public", private: "Private",
      makePublic: "Make public", makePrivate: "Make private",
      owner: "owner", pick: "Choose a teammate…", invite: "Invite",
    },
    io: {
      export: "Export CSV", import: "Import CSV", importContacts: "Import contacts",
      chooseFile: "Choose a .csv file", importDone: "contacts imported", errors: "errors",
      exportData: "Export", hint: "CSV with columns: first_name, last_name, email, phone, title, status, company",
    },
    notif: { title: "Notifications", empty: "You're all caught up", markAll: "Mark all read" },
    util: {
      title: "Team utilization", week: "This week", month: "This month",
      user: "Team member", total: "Total hours", billable: "Billable hours",
      amount: "Billable amount", rate: "Utilization", teamTotal: "Team total",
      noData: "No time logged in this period",
    },
    dealFlow: {
      title: "Deal flow",
      won: "Won", lost: "Lost", wonLostRatio: "Won/lost ratio",
      avgPasses: "Avg. passes to won",
      avgDaysPerStage: "Average days per stage",
      noData: "No deals yet",
    },
    timeline: {
      title: "Lifecycle",
      created: "Created", deleted: "Deleted", updated: "Updated",
      claimed: "Claimed by", stage_changed: "Stage changed",
      status_changed: "Status changed", role_changed: "Role changed",
      visibility_changed: "Visibility changed", owner_changed: "Owner changed",
      ball_in_court_changed: "Ball in court changed", activity_logged: "Activity logged",
      noEvents: "No history yet",
    },
    email: {
      log: "Log email", subject: "Subject", body: "Message", direction: "Direction",
      inbound: "Received", outbound: "Sent", logged: "Email logged",
    },
    roles: { admin: "Admin", manager: "Manager", user: "User", guest: "Guest" },
    statuses: {
      lead: "Lead", prospect: "Prospect", customer: "Customer", inactive: "Inactive",
      qualified: "Qualified", proposal: "Proposal", negotiation: "Negotiation",
      won: "Won", lost: "Lost", planning: "Planning", active: "Active",
      on_hold: "On hold", completed: "Completed", cancelled: "Cancelled",
      low: "Low", medium: "Medium", high: "High",
      call: "Call", email: "Email", meeting: "Meeting", task: "Task", note: "Note",
    },
  },
};

const hu = {
  translation: {
    app: { tagline: "Ügyfélkapcsolat-kezelő" },
    nav: {
      dashboard: "Irányítópult",
      contacts: "Kapcsolatok",
      companies: "Cégek",
      deals: "Üzletek",
      projects: "Projektek",
      activities: "Tevékenységek",
      meetings: "Meetingek",
      calendar: "Naptár",
      utilization: "Kihasználtság",
      dealFlow: "Deal-flow",
      users: "Felhasználók",
      settings: "Beállítások",
      logout: "Kijelentkezés",
      more: "Több",
    },
    onboarding: {
      replayTour: "Bevezető újranézése",
      back: "Vissza",
      close: "Bezárás",
      finish: "Befejezés",
      next: "Következő",
      skip: "Kihagyás",
      welcomeTitle: "Üdvözlünk a {{brand}} rendszerben!",
      welcomeBody: "Nézzük végig együtt az egész alkalmazást — pár perc, és tudni fogod, mi hol van. Bármikor kiléphetsz a bevezetőből.",
      metricsTitle: "Az irányítópultod",
      metricsBody: "Kapcsolatok, nyitott üzletek, pipeline érték, cashflow, nyitott feladatok — egy pillantással.",
      aiTitle: "AI parancssor",
      aiBody: "Írj be egy egyszerű parancsot, például: \"hozz létre egy 5000 {{currency}} értékű üzletet az Acme-nek\" — az AI elvégzi helyetted.",
      pipelineTitle: "Pipeline grafikon",
      pipelineBody: "Üzleteid szakaszonként, {{currencies}} pénznemben.",
      tasksTitle: "Közelgő feladatok",
      tasksBody: "Nyitott feladataid és követéseid, határidő szerint rendezve.",
      navTitle: "A navigáció",
      navBody: "A CRM minden része innen érhető el. Most sorra vesszük őket.",
      navMobileTitle: "A navigáció",
      navMobileBody: "A fő menüpontok ebben a sávban vannak. Most sorra vesszük őket.",
      moreTitle: "A „Több” fül",
      moreBody: "A Cégek, a Tevékenységek — és adminként a Felhasználók és a Beállítások — a Több mögött vannak: a sávban nincs rájuk hely.",
      contactsAddTitle: "Kapcsolat felvétele",
      contactsAddBody: "Az „Új kapcsolat” egy személyt hoz létre: név, e-mail, telefon, a cége, és egy státusz a leadtől az ügyfélig.",
      contactsFindTitle: "Emberek keresése",
      contactsFindBody: "Keress név vagy e-mail alapján, szűrj státuszra, és csoportosítsd a listát státusz vagy cég szerint. A kártya/lista váltó fent található.",
      companiesAddTitle: "Cég felvétele",
      companiesAddBody: "Az „Új cég” a kapcsolataid mögött álló szervezetet rögzíti. Ha megadod a weboldalát, a CRM letölti hozzá a logót.",
      companiesViewsTitle: "Kártyák vagy lista",
      companiesViewsBody: "Válts kártya- és listanézet között, és szűrj iparágra, méretre, vagy arra, hogy van-e rögzített weboldal.",
      dealsAddTitle: "Üzlet felvétele",
      dealsAddBody: "Az „Új üzlet”-nél lesz a leadből szám: név, cég, kapcsolattartó, érték és pénznem, szakasz, és hogy honnan jött. Ha gazdátlanul hagyod, a közös inboxba kerül, ahonnan bárki elviheti.",
      dealsBoardTitle: "A pipeline-od",
      dealsBoardBody: "Tábla vagy lista nézet. A táblán húzd a kártyát másik oszlopba az üzlet léptetéséhez — ha megnyered, a projekt automatikusan létrejön hozzá.",
      dealsInboxTitle: "Kinél van a labda",
      dealsInboxBody: "Szűrj a gazdátlan leadekre, azokra, ahol nálunk van a labda, vagy amelyek elakadtak. Nyiss meg egy üzletet, hogy elvidd, átadd, vagy elolvasd a teljes időrendjét.",
      projectsTitle: "Projektek",
      projectsBody: "A megnyert üzletekből projekt lesz. Kövesd a büdzsét, rögzíts hozzá órákat, és számlázz mérföldkövek szerint — az előleg–végszámla felosztástól a teljes mérföldkő-tervig.",
      activitiesTitle: "Tevékenységek",
      activitiesBody: "Hívások, e-mailek és feladatok, bármely kapcsolathoz, üzlethez vagy projekthez rögzítve. Az, hogy egy tevékenység bejövő vagy kimenő, mozgatja az üzletnél a labdát.",
      calendarTitle: "Naptár",
      calendarBody: "Minden, amihez dátum tartozik, egy helyen — váltogathatsz hónap, hét, 3 napos és napi nézet között. Kattints egy eseményre a részletekért, vagy húzd át más időpontra.",
      reportsTitle: "Riportok",
      reportsBody: "Csapat-kihasználtság hetes vagy hónapos bontásban, és pipeline-elemzés a Deal-flow alatt: nyert/vesztett arány, és hogy meddig állnak az üzletek az egyes szakaszokban.",
      notifTitle: "Értesítések",
      notifBody: "Itt kapsz jelzést az elakadt üzletekről, gazdátlan leadekről és lejárt feladatokról.",
      themeTitle: "Nyelv és téma",
      themeBody: "Bármikor válthatsz magyar és angol, illetve világos és sötét téma között.",
      closingTitle: "Ennyi volt a bevezető!",
      closingBody: "A ? gombbal bármikor újranézheted. Sikeres értékesítést!",
    },
    auth: {
      welcome: "Üdvözlünk újra",
      subtitle: "Jelentkezz be a {{brand}} munkaterületre",
      pitch: "A mobil-első CRM kis csapatoknak. Kapcsolatok, üzletek, projektek és egy AI parancssáv — egy helyen.",
      email: "E-mail",
      password: "Jelszó",
      name: "Teljes név",
      signIn: "Bejelentkezés",
      signUp: "Fiók létrehozása",
      noAccount: "Nincs még fiókod?",
      haveAccount: "Van már fiókod?",
      register: "Regisztráció",
      login: "Bejelentkezés",
    },
    common: {
      search: "Keresés…",
      add: "Hozzáadás",
      new: "Új",
      edit: "Szerkesztés",
      delete: "Törlés",
      save: "Mentés",
      cancel: "Mégse",
      create: "Létrehozás",
      update: "Frissítés",
      confirm: "Megerősítés",
      loading: "Betöltés…",
      noResults: "Nincs találat",
      actions: "Műveletek",
      all: "Összes",
      close: "Bezárás",
      confirmDelete: "Biztosan törölni szeretnéd ezt?",
      readOnly: "Csak olvasási jog",
    },
    dashboard: {
      title: "Irányítópult",
      greeting: "Üdvözlünk újra",
      contacts: "Kapcsolatok",
      companies: "Cégek",
      openDeals: "Nyitott üzletek",
      activeProjects: "Aktív projektek",
      pipelineValue: "Pipeline érték",
      wonValue: "Megnyert ebben az időszakban",
      cashFlow: "Számlázva, fizetésre vár",
      openTasks: "Nyitott feladatok",
      pipelineByStage: "Pipeline szakaszonként",
      contactsByStatus: "Kapcsolatok státusz szerint",
      projectsByStatus: "Projektek státusz szerint",
      upcomingTasks: "Közelgő feladatok",
    },
    contact: {
      title: "Kapcsolatok",
      first_name: "Keresztnév",
      last_name: "Vezetéknév",
      email: "E-mail",
      phone: "Telefon",
      jobtitle: "Beosztás",
      status: "Státusz",
      company: "Cég",
      notes: "Jegyzetek",
      tags: "Címkék",
      newContact: "Új kapcsolat",
      editContact: "Kapcsolat szerkesztése",
      regularReferrer: "Rendszeres beajánló",
      referrals: "Beajánlások",
      referralsCount: "Beajánlott dealek",
      referralsWon: "Megnyert",
      referrersOnly: "Csak beajánlók",
      linkedinUrl: "LinkedIn",
      grid: "Rács",
      list: "Lista",
      sortBy: "Rendezés",
      groupBy: "Csoportosítás",
      groupNone: "Nincs csoportosítás",
      groupByStatus: "Státusz szerint",
      groupByCompany: "Cég szerint",
      sort_newest: "Legújabb elöl",
      sort_oldest: "Legrégebbi elöl",
      sort_name_asc: "Név (A–Z)",
      sort_name_desc: "Név (Z–A)",
      sort_company_asc: "Cég (A–Z)",
      sort_status_pipeline: "Státusz (folyamat sorrendben)",
      allCompanies: "Minden cég",
      uncategorized: "Nincs cég",
    },
    company: {
      title: "Cégek",
      name: "Cégnév",
      industry: "Iparág",
      website: "Weboldal",
      phone: "Telefon",
      email: "E-mail",
      address: "Cím",
      size: "Cégméret",
      notes: "Jegyzetek",
      newCompany: "Új cég",
      editCompany: "Cég szerkesztése",
      refreshLogo: "Logó frissítése",
      grid: "Rács",
      list: "Lista",
      sortBy: "Rendezés",
      groupBy: "Csoportosítás",
      groupNone: "Nincs csoportosítás",
      groupByIndustry: "Iparág szerint",
      groupBySize: "Cégméret szerint",
      sort_newest: "Legújabb elöl",
      sort_oldest: "Legrégebbi elöl",
      sort_name_asc: "Név (A–Z)",
      sort_name_desc: "Név (Z–A)",
      sort_industry_asc: "Iparág (A–Z)",
      sort_size_asc: "Cégméret (A–Z)",
      hasWebsite: "Van weboldala",
      allIndustries: "Minden iparág",
      allSizes: "Minden cégméret",
      uncategorized: "Nincs megadva",
    },
    deal: {
      title: "Üzletek",
      name: "Üzlet neve",
      value: "Érték",
      currency: "Pénznem",
      stage: "Szakasz",
      probability: "Valószínűség",
      expectedClose: "Várható zárás",
      company: "Cég",
      contact: "Kapcsolat",
      notes: "Jegyzetek",
      newDeal: "Új üzlet",
      editDeal: "Üzlet szerkesztése",
      board: "Tábla",
      list: "Lista",
      total: "Összesen",
      owner: "Felelős",
      unassigned: "Gazdátlan",
      claim: "Elvállalom",
      source: "Forrás",
      source_inbound: "Bejövő",
      source_outreach: "Kimenő megkeresés",
      source_referral: "Beajánlás",
      source_other: "Egyéb",
      ballInCourt: "Kinél a labda",
      ballInCourtFilter: "Nálunk a labda",
      ballInCourt_us: "Nálunk",
      ballInCourt_them: "Kliensnél",
      ballInCourt_none: "—",
      leadType: "Lead-típus",
      leadType_single: "Egyoldalú",
      leadType_double: "Kétoldalú",
      leadTypeHint: "Kétoldalú: a fizető/szerződő fél más cég vagy kapcsolattartó, mint akivel a napi munka során egyeztetsz.",
      contractCompany: "Szerződő cég",
      contractContact: "Szerződő kapcsolattartó",
      referredBy: "Beajánlotta",
      stale: "Kihűlőben",
    },
    project: {
      title: "Projektek",
      name: "Projekt neve",
      description: "Leírás",
      status: "Státusz",
      priority: "Prioritás",
      budget: "Költségvetés",
      startDate: "Kezdés",
      endDate: "Befejezés",
      company: "Cég",
      newProject: "Új projekt",
      editProject: "Projekt szerkesztése",
      followUpDays: "Utókövetés késleltetése (nap)",
      closedAt: "Lezárva",
    },
    followup: {
      title: "Utókövetés",
      hint: "Rögzítsd, mennyire volt elégedett a kliens, és jelezz beajánlást, ha említettek egyet.",
      satisfaction: "Elégedettség (1-5)",
      hasReferral: "Említettek beajánlást",
      referredContact: "Beajánlott kapcsolat",
      complete: "Utókövetés lezárása",
      readOnlyHint: "Írási joggal rendelkező felhasználóra vár, aki lezárhatja.",
    },
    milestone: {
      title: "Mérföldkövek",
      add: "Mérföldkő hozzáadása",
      edit: "Mérföldkő szerkesztése",
      name: "Mérföldkő neve",
      dueDate: "Határidő",
      byAmount: "Fix összeg",
      byPercentage: "Százalék",
      amount: "Összeg",
      percentage: "Százalék",
      valueRequired: "Adj meg egy összeget vagy százalékot.",
      none: "Még nincs mérföldkő",
      budgetMismatch: "A mérföldkövek összege nem egyezik a projekt költségvetésével -- érdemes ellenőrizni.",
      totalVsBudget: "{{total}} / {{budget}} költségvetés",
      template: "Mérföldkő sablon",
      template_single_final: "Egy összegben, teljesítéskor",
      template_deposit_final: "Előleg + záró fizetés",
      template_milestones: "Egyéni (üresen kezd)",
      workStatus_in_progress: "Folyamatban",
      workStatus_client_review: "Kliens jóváhagyására vár",
      workStatus_accepted: "Elfogadva",
      paymentStatus_not_due: "Még nem esedékes",
      paymentStatus_invoiceable: "Számlázható",
      paymentStatus_invoiced: "Számlázva",
      paymentStatus_paid: "Kifizetve",
    },
    activity: {
      title: "Tevékenységek",
      type: "Típus",
      directionLabel: "Irány",
      direction: { inbound: "Bejövő", outbound: "Kimenő", internal: "Belső" },
      subject: "Tárgy",
      description: "Leírás",
      dueDate: "Határidő",
      completed: "Kész",
      newActivity: "Új tevékenység",
      editActivity: "Tevékenység szerkesztése",
      logActivity: "Tevékenység rögzítése",
      noDirection: "—",
    },
    users: {
      title: "Felhasználók és szerepek",
      name: "Név",
      email: "E-mail",
      role: "Szerep",
      status: "Státusz",
      active: "Aktív",
      disabled: "Letiltva",
      newUser: "Új felhasználó",
      editUser: "Felhasználó szerkesztése",
      password: "Jelszó",
    },
    branding: {
      identityGroup: "Arculat",
      metadataGroup: "Böngésző- és AI-metaadatok",
      metadataHint: "A böngészőfül címe, a keresőkben megjelenő leírás, és a cégedről szóló egysoros leírás, amit az AI-funkciók megkapnak.",
      coloursGroup: "Színek",
      coloursHint: "Azonnal érvényesül az egész alkalmazásban. Nézd meg világos és sötét témában is az előnézetben — ami fehéren éles, az a sötét háttéren eltűnhet.",
      domainsGroup: "Domainek",
      domainsHint: "Az ezekhez a domainekhez tartozó résztvevőket belsősnek tekintjük, mindenki mást ügyféloldalinak.",
      moneyGroup: "Pénz",
      moneyHint: "Az itt elfogadott valuták, és hogy az új üzletek és projektek melyiket használják alapértelmezésben. Az alapértelmezett szerepeljen a felsoroltak között.",
      title: "Megjelenés",
      desc: "Név, logo-szöveg, színek és pénznemek ehhez a telepítéshez. Azonnal érvényes — nincs újraépítés.",
      orgName: "Szervezet neve",
      tagline: "Szlogen",
      taglinePlaceholder: "Hagyd üresen a lefordított alapértékhez",
      wordmarkText: "Logo szöveg",
      wordmarkAccent: "Logo kiemelés",
      preview: "Előnézet",
      previewApp: "Alkalmazás",
      previewLogin: "Bejelentkezés",
      previewMobile: "Mobil",
      previewLight: "Előnézet világos témában",
      previewDark: "Előnézet sötét témában",
      previewHint: "A még nem mentett beállítások látványterve. Válts felületet és témát, hogy mentés előtt lásd: a szín mindenhol működik-e.",
      discovery: {
        title: "Kitöltés weboldalról",
        desc: "Beolvassuk az oldal nyilvános márkaadatait — nevet, színeket, logókat — és javaslatot tesznek. Semmi nem változik, amíg nem választod ki, mit tartasz meg.",
        websiteLabel: "Céges weboldal",
        fetch: "Oldal beolvasása",
        fetching: "Beolvasás…",
        found: "{{count}} javaslat érhető el",
        found_plural: "{{count}} javaslat érhető el",
        selectAll: "Összes kijelölése",
        selectNone: "Kijelölés törlése",
        apply: "{{count}} kijelölt alkalmazása",
        applying: "Alkalmazás…",
        discard: "Elvetés",
        applyHint: "A képeket letöltjük és itt tároljuk, így a bejelentkezési oldal soha nem függ más oldal elérhetőségétől.",
        nothingNew: "Nincs új javaslat — az oldal adatai már megegyeznek a beállításaiddal.",
        partial: "Ez túl sokáig tartott, az eredmény hiányos. Próbáld újra beolvasni az oldalt.",
      },
      documentTitle: "Böngészőfül címe",
      orgDescription: "Mivel foglalkozik a szervezet",
      orgDescriptionPlaceholder: "pl. műszaki tanácsadó cég — az AI promptokban használjuk",
      primary: "Elsődleges szín",
      primaryHover: "Elsődleges (hover)",
      glow: "Kiemelő szín",
      logoIconUrl: "Logó ikon URL",
      faviconUrl: "Favicon URL",
      loginImageUrl: "Belépő kép URL",
      loginImagePlaceholder: "Üresen a színeidből készült gradiens",
      internalDomains: "Belső e-mail domainek",
      currencies: "Pénznemek",
      defaultCurrency: "Alapértelmezett pénznem",
      images: "Képek",
      logoUrl: "Teljes logó",
      appleTouchIconUrl: "Apple touch ikon",
      themeColor: "Mobil böngészősáv",
      metaDescription: "Meta leírás",
      googleHd: "Google fiók-tipp",
      upload: "Feltöltés",
      removeUpload: "Feltöltött fájl eltávolítása",
      imagesHint: "PNG, JPEG, WebP, GIF vagy ICO — max. 512 KB (a belépő képnél 3 MB). SVG nem engedélyezett: szkriptet tartalmazhat, ezek a képek pedig belépés előtt is láthatók. A feltöltés eltávolítása visszaállítja a beépített alapértéket.",
      hint: "Egy mező kitörlése visszaállítja a környezeti vagy beépített értéket.",
    },
    capabilities: {
      visibilityGroup: "Új rekordok",
      visibilityHint: "Egy új üzlet vagy projekt mindenki számára látható legyen-e, vagy csak a felelősnek és a hozzáadott embereknek.",
      matrixGroup: "Képességmátrix",
      matrixHint: "Az admin és a menedzser szándékosan fix. A felhasználók kezelése és e mátrix szerkesztése mindig csak adminnak jár, és itt nem is szerepel.",
      title: "Hozzáférés-kezelés",
      desc: "Mit tehet a user és guest szerepkör. Admin és manager mindig teljes hozzáférést kap.",
      capability: "Képesség",
      view_financials: "Pénzügyi értékek látása (deal érték, budget, óradíj)",
      manage_deals: "Dealek létrehozása/szerkesztése/törlése",
      manage_projects: "Projektek létrehozása/szerkesztése/törlése",
      invite_members: "Tagok meghívása private dealhez/projekthez",
      set_visibility: "Deal/projekt láthatóságának módosítása",
      reassign_owner: "Deal/projekt felelősének átállítása",
      reassignOwnerNote: "(még sehol nincs érvényesítve -- a kapcsoló egyelőre nem csinál semmit)",
      view_all_reports: "Összesített riportok megtekintése (nem csak a sajátja)",
      defaultVisibility: "Új dealek/projektek alap láthatósága",
      public: "Nyilvános (mindenki látja)",
      private: "Privát (csak meghívottak)",
    },
    thresholds: {
      title: "Határértékek",
      desc: "Meddig állhat valami, mielőtt a CRM megjelöli.",
      businessDaysNote: "Munkanapokban számolva (hétfőtől péntekig); a munkaszüneti napokat nem vesszük figyelembe.",
      unassigned: "Fel nem vett érdeklődő",
      unassignedHint: "Ennyi idő után, felelős nélkül, az érdeklődőt jelezzük a menedzsereknek és adminoknak.",
      awaitingResponse: "Ránk vár a válasz",
      awaitingResponseHint: "Ennyi idő után, ha nálunk van a labda, emlékeztetjük az üzlet felelősét.",
      stale: "Az üzlet beragad",
      staleHint: "Ennyi idő után kapcsolatfelvétel nélkül az üzletet beragadtnak jelöljük a pipeline-ban.",
    },
    settings: {
      title: "Beállítások",
      nav: {
        branding: "Márka",
        ai: "AI",
        permissions: "Jogosultságok",
        thresholds: "Határértékek",
        automation: "Automatizálás",
        apiKeys: "API-kulcsok",
        integrations: "Integrációk",
      },
      unsaved: "Nem mentett módosításaid vannak",
      discard: "Elvetés",
      saving: "Mentés…",
      savedFlash: "A módosítások elmentve",
      aiCredentials: "Hitelesítés",
      aiModel: "Modell",
      aiKeyHint: "Titkosítva, a saját adatbázisodban tároljuk, és csak a szerveren használjuk. Ingyenes kulcs itt:",
      housekeepingManual: "Futtatás most",
      housekeepingManualHint: "A feladat minden éjjel 02:00-kor magától lefut. A kézi futtatás arra jó, hogy lásd, mit végez, vagy hogy leállás után behozd a lemaradást.",
      integrations: "Integrációk",
      integrationsDesc: "Kapcsold össze a CRM-et azokkal az eszközökkel, amelyeket a csapat már használ.",
      integrationsRequires: "Mindkettőhöz Google Cloud OAuth hitelesítő adatok (Client ID és Secret) kellenek, mielőtt bekapcsolhatók.",
      integrationGmail: "Gmail",
      integrationGmailDesc: "E-mailek automatikus naplózása kapcsolatokhoz és üzletekhez.",
      integrationCalendar: "Google Naptár",
      integrationCalendarDesc: "A megbeszélések mindkét irányban szinkronban maradnak a naptárral.",
      integrationSignInGroup: "Belépés Google-fiókkal",
      integrationSignInNote: "A Google-belépést a szerveren kell beállítani, nem itt — ehhez a GOOGLE_CLIENT_ID és a GOOGLE_WORKSPACE_DOMAIN értékek kellenek. A hozzá tartozó fiókválasztó tipp a Márka szekció „Google domain” mezője.",
      saLeastPrivilege: "Minden fióknak csak azokat a képességeket add meg, amikre az ügynökének valóban szüksége van. Minden írását naplózzuk ehhez a fiókhoz kötve.",
      saEmptyHint: "Akkor hozz létre egyet, ha külső AI-ügynök vagy szkript férne hozzá a CRM-hez az API-n keresztül.",
      ai: "AI Asszisztens (OpenRouter)",
      aiDesc: "Csatlakoztasd az OpenRouter API kulcsod az AI parancsokhoz.",
      apiKey: "OpenRouter API kulcs",
      model: "Modell",
      configured: "Csatlakoztatva",
      notConfigured: "Nincs csatlakoztatva",
      google: "Google Workspace",
      googleDesc: "Gmail és Naptár csatlakoztatása (Google Cloud hitelesítés szükséges).",
      appearance: "Megjelenés",
      language: "Nyelv",
      theme: "Téma",
      light: "Világos",
      dark: "Sötét",
      housekeeping: "Napi karbantartó job",
      housekeepingDesc: "Utókövetési feladatok létrehozása, kihűlő dealek jelzése, értesítés-szinkron -- alapból naponta egyszer, automatikusan fut.",
      housekeepingRun: "Futtatás most",
      housekeepingRunning: "Fut…",
      housekeepingFollowUps: "Létrehozott utókövetési feladatok",
      housekeepingStale: "Módosult kihűlő-jelzők",
      housekeepingNotifications: "Szinkronizált felhasználók",
      housekeepingSkipped: "Kihagyva -- már fut egy másik példány.",
      serviceAccounts: "Szolgáltatásfiókok",
      serviceAccountsDesc: "API-kulcsos identitások külső eszközöknek és AI-ügynököknek (pl. az MCP szerver) -- ugyanaz a szerepkör-/jogosultsági modell, mint egy embernél.",
      newServiceAccount: "Új szolgáltatásfiók",
      saName: "Név",
      saActive: "Aktív",
      saRevoked: "Visszavonva",
      saKeyWarning: "Másold ki most a kulcsot -- többé nem jelenik meg.",
      saKeyCloseConfirm: "Még nem másoltad ki ezt a kulcsot. Többé nem jelenik meg -- mégis bezárod?",
      saCopy: "Másolás",
      saCopied: "Kimásolva",
      saRevokeConfirm: "Visszavonod ezt a szolgáltatásfiókot? Ami az API-kulcsát használja, azonnal leáll.",
      noServiceAccounts: "Még nincs szolgáltatásfiók.",
    },
    ai: {
      placeholder: "Kérd az AI-t: hozz létre projektet, kapcsolatot, üzletet vagy összegzést…",
      title: "AI Parancs",
      run: "Futtatás",
      hint: "Próbáld: „Hozz létre egy RAG Pilot projektet az Acme-nek, magas prioritással”",
      thinking: "Gondolkodom…",
      created: "Létrehozva",
      notConfigured: "Az AI nincs beállítva. Adj meg egy OpenRouter kulcsot a Beállításokban.",
    },
    time: {
      title: "Idő és ráfordítás",
      internalHint: "Csak belső költség-/kihasználtságkövetés -- a kliens felé a fenti mérföldkövek számláznak, nem az óradíj.",
      logged: "Rögzített", estimated: "Becsült", remaining: "Hátralévő",
      addTime: "Idő rögzítése", hours: "Óra", billable: "Elszámolható (belső)",
      rate: "Óradíj", amount: "Belső költség", noEntries: "Még nincs rögzített idő",
      logHours: "Órák rögzítése",
    },
    health: {
      label: "Állapot", on_track: "Ütemben", at_risk: "Veszélyben",
      over_budget: "Túllépve", completed: "Befejezett", cancelled: "Törölve",
    },
    detail: {
      back: "Vissza", overview: "Áttekintés", deals: "Üzletek", contacts: "Kapcsolatok",
      projects: "Projektek", timeline: "Tevékenység idővonal", noActivities: "Még nincs tevékenység",
      noItems: "Itt még nincs semmi",
    },
    calendar: {
      title: "Naptár",
      today: "Ma",
      previous: "Előző",
      next: "Következő",
      noTasks: "Nincs feladat ezen a napon",
      viewLabel: "Naptárnézet",
      view: { month: "Hónap", week: "Hét", day3: "3 nap", day: "Nap" },
      viewKey: { month: "M", week: "W", day3: "X", day: "D" },
      allDay: "Egész nap",
      allDayEvent: "Egész napos — nincs pontos idő",
      startTime: "Kezdés",
      endTime: "Befejezés",
      newEvent: "Új esemény",
      editEvent: "Esemény szerkesztése",
      typeLocked: "Ez egy meeting — a típusát a meeting oldalán tudod módosítani.",
      subjectRequired: "Adj neki tárgyat, hogy később is felismerd.",
      dateRequired: "Válassz érvényes dátumot.",
      endBeforeStart: "A befejezésnek a kezdés után kell lennie.",
      hideCompleted: "Elvégzettek elrejtése",
      hidden: "{{count}} elem szűrve",
      more: "+{{count}} további",
      eventCount: "{{count}} elem",
      markDone: "Kész",
      markOpen: "Újranyitás",
      openMeeting: "Meeting megnyitása",
      openDeal: "Deal megnyitása",
      openProject: "Projekt megnyitása",
      openContact: "Kapcsolat megnyitása",
      openCompany: "Cég megnyitása",
      saved: "Mentve",
      deleted: "Törölve",
      moved: "Áthelyezve",
      emptyHint: "Még semmi nincs beütemezve — kattints bármelyik napra.",
      emptyHintReadOnly: "Ebben az időszakban nincs bejegyzés.",
      dragHint: "Húzd át az eseményt az áthelyezéshez.",
      undated: "{{count}} elemnek nincs dátuma, ezért itt nem látszik:",
    },
    meet: {
      title: "Meetingek",
      allStates: "Minden állapot",
      state: { draft: "Vázlat", processing: "Feldolgozás", ready: "Kész", failed: "Hibás" },
      emptyTitle: "Még nincs meeting",
      emptyHint: "Húzz be egy meeting-leiratot fentre — Teamsből exportált .docx vagy .vtt, esetleg sima szöveg. A meetinget körülötte hozzuk létre, és kiszedjük belőle az összefoglalót meg a feladatokat.",
      openItems: "{{count}} nyitott",
      dropHere: "Húzd ide a leiratot, vagy kattints a fájlválasztáshoz",
      dropHint: "Teams export (.docx, .vtt) vagy sima szöveg (.txt, .md)",
      uploading: "Feltöltés…",
      pasteInstead: "Szöveg beillesztése",
      pasteTitle: "Leirat beillesztése",
      pasteHint: "Illeszd be a meeting leiratát. A beszélők nevét hagyd benne — abból tudjuk a résztvevőket kapcsolatokhoz kötni.",
      pastePlaceholder: "Anna: Sziasztok, kezdjük is el…",
      pastedTitle: "Beillesztett leirat",
      startExtraction: "Feldolgozás indítása",
      backToList: "Összes meeting",
      notFound: "Ez a meeting nem létezik, vagy nincs hozzá jogosultságod.",
      deal: "Üzlet",
      saved: "Mentve",
      processingHint: "Készül az összefoglaló és a feladatlista — általában egy percen belül megvan.",
      failedTitle: "A feldolgozás nem sikerült",
      failedHint: "Újrapróbálhatod, cserélheted a leiratot, vagy egyszerűen megírhatod kézzel a jegyzetet.",
      sensitiveHidden: "Ez a meeting érzékenyként van jelölve — az összefoglalót és a leiratot csak a felelős és az adminok látják.",
      noTranscriptYet: "Még nincs leirat",
      summary: "Összefoglaló",
      humanEdited: "Ember szerkesztette — az eredeti AI-változat megmarad a feldolgozási előzményben.",
      actionItems: "Feladatok",
      alreadyAccepted: "{{count}} már taskká lett alakítva",
      itemState: { proposed: "Javasolt", accepted: "Elfogadva", dismissed: "Elvetve" },
      accept: "Elfogadás",
      viewTask: "Task megnyitása",
      ownerDefault: "Meeting felelőse",
      createTask: "Task létrehozása",
      taskCreated: "Task létrehozva",
      decisions: "Döntések",
      risks: "Kockázatok és nyitott kérdések",
      transcript: "Leirat",
      chars: "karakter",
      regenerate: "Újragenerálás",
      replaceTranscript: "Jobb leirat feltöltése",
      editTitle: "Meeting szerkesztése",
      subject: "Cím",
      startsAt: "Kezdés",
      endsAt: "Vége",
      direction: "Kinél van a labda?",
      dirInbound: "Nálunk — mi tartozunk a következő lépéssel",
      dirOutbound: "Náluk — a kliensre várunk",
      dirInternal: "Belső meeting",
      participants: "Résztvevők",
      addTeammate: "Csapattag hozzáadása…",
      sensitive: "Érzékenyként jelölés",
      sensitiveHint: "Az összefoglalót és a leiratot a felelősre és az adminokra szűkíti.",
      confirmDelete: "Törlöd ezt a meetinget a leiratával és a feldolgozásaival? A belőle már létrehozott taskok megmaradnak.",
      dealCardTitle: "Meetingek",
      seeAll: "Összes",
      noneForDeal: "Ehhez az üzlethez még nincs rögzített meeting.",
    },
    members: {
      title: "Hozzáférés", public: "Nyilvános", private: "Privát",
      makePublic: "Nyilvánossá tétel", makePrivate: "Privátra állítás",
      owner: "felelős", pick: "Válassz csapattagot…", invite: "Meghívás",
    },
    io: {
      export: "CSV export", import: "CSV import", importContacts: "Kapcsolatok importálása",
      chooseFile: "Válassz .csv fájlt", importDone: "kapcsolat importálva", errors: "hiba",
      exportData: "Exportálás", hint: "CSV oszlopok: first_name, last_name, email, phone, title, status, company",
    },
    notif: { title: "Értesítések", empty: "Minden naprakész", markAll: "Összes olvasottnak" },
    util: {
      title: "Csapat kihasználtság", week: "Ezen a héten", month: "Ebben a hónapban",
      user: "Csapattag", total: "Összes óra", billable: "Számlázható óra",
      amount: "Számlázható összeg", rate: "Kihasználtság", teamTotal: "Csapat összesen",
      noData: "Nincs rögzített idő ebben az időszakban",
    },
    dealFlow: {
      title: "Deal-flow",
      won: "Megnyert", lost: "Elvesztett", wonLostRatio: "Nyert/vesztett arány",
      avgPasses: "Átlagos passz-szám megnyerésig",
      avgDaysPerStage: "Átlagos napok szakaszonként",
      noData: "Még nincsenek dealek",
    },
    timeline: {
      title: "Életút",
      created: "Létrehozva", deleted: "Törölve", updated: "Frissítve",
      claimed: "Elvállalta", stage_changed: "Szakaszváltás",
      status_changed: "Státuszváltás", role_changed: "Szerepkör-váltás",
      visibility_changed: "Láthatóság-váltás", owner_changed: "Felelős-váltás",
      ball_in_court_changed: "Labda-váltás", activity_logged: "Tevékenység rögzítve",
      noEvents: "Még nincs előzmény",
    },
    email: {
      log: "E-mail rögzítése", subject: "Tárgy", body: "Üzenet", direction: "Irány",
      inbound: "Beérkezett", outbound: "Elküldött", logged: "E-mail rögzítve",
    },
    roles: { admin: "Admin", manager: "Menedzser", user: "Felhasználó", guest: "Vendég" },
    statuses: {
      lead: "Érdeklődő", prospect: "Lehetőség", customer: "Ügyfél", inactive: "Inaktív",
      qualified: "Minősített", proposal: "Ajánlat", negotiation: "Tárgyalás",
      won: "Megnyert", lost: "Elveszett", planning: "Tervezés", active: "Aktív",
      on_hold: "Felfüggesztve", completed: "Befejezett", cancelled: "Törölve",
      low: "Alacsony", medium: "Közepes", high: "Magas",
      call: "Hívás", email: "E-mail", meeting: "Találkozó", task: "Feladat", note: "Jegyzet",
    },
  },
};

i18n.use(initReactI18next).init({
  resources: { en, hu },
  lng: localStorage.getItem("locale") || "en",
  fallbackLng: "en",
  interpolation: { escapeValue: false },
});

export default i18n;
